import { Head } from "@inertiajs/react";
import { Badge, Box, Button, Flex, Section, Text, Theme } from "@radix-ui/themes";
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { PanelRightClose, X } from "lucide-react";
import { TrackManagerTrack } from "@/types/shared";
import TracksTable from "@/shared/tables/track_manager/TracksTable";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import TrackDetailPanel from "@/shared/track_manager/TrackDetailPanel";
import ExpandableCards from "@/shared/manager/ExpandableCards";
import useIsMobile from "@/shared/hooks/useIsMobile";

interface Props {
  tracks: TrackManagerTrack[];
}

export default function TrackManagerTracksIndex({ tracks }: Props) {
  const isMobile = useIsMobile();
  const [selectedRow, setSelectedRow] = useState<TrackManagerTrack | null>(
    null,
  );

  if (isMobile) {
    return (
      <div className="flex">
        <Head title="My Tracks" />
        <div className="grow min-w-0">
          <ManagerPageHeader title="Tracks" />
          <Section size="1">
            <Box px="3">
              <ExpandableCards
                items={tracks}
                getKey={(track) => track.id}
                emptyMessage="You don't manage any tracks yet."
                renderSummary={(track) => (
                  <Flex direction="column" gap="1">
                    <p className="truncate text-[15px] font-bold leading-tight">
                      {track.name}
                    </p>
                    <span className="truncate text-sm text-gray11">
                      {track.city}, {track.state}
                    </span>
                    <Flex gap="2" mt="1" wrap="wrap">
                      <Badge variant="soft" color="gray">
                        {track.event_count} {track.event_count === 1 ? "event" : "events"}
                      </Badge>
                      {track.draft_count > 0 && (
                        <Badge variant="soft" color="amber">
                          {track.draft_count} draft
                        </Badge>
                      )}
                    </Flex>
                  </Flex>
                )}
                renderDetail={(track) => <TrackDetailPanel track={track} compact />}
              />
            </Box>
          </Section>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Head title="My Tracks" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Tracks" />
        <Dialog.Root
          open={selectedRow !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedRow(null);
          }}
        >
          <Section size="1">
            {tracks.length === 0 ? (
              <Box px="4">
                <Text color="gray">You don't manage any tracks yet.</Text>
              </Box>
            ) : (
              <Box style={{ overflowX: "auto" }}>
                <TracksTable tracks={tracks} setSelectedRow={setSelectedRow} />
              </Box>
            )}
          </Section>

          <Dialog.Portal>
            <Theme>
              <Dialog.Overlay
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "var(--color-overlay)",
                }}
              />
              <Dialog.Content
                style={{
                  position: "fixed",
                  top: 0,
                  right: 0,
                  width: "min(440px, 100vw)",
                  height: "100vh",
                  background: "var(--color-panel-solid)",
                  padding: "var(--space-5)",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <Flex justify="between" align="center">
                  <Dialog.Title asChild>
                    <Text size="5" weight="bold">
                      Track Detail
                    </Text>
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <Button variant="ghost" size="1">
                      <X size="16px" />
                    </Button>
                  </Dialog.Close>
                </Flex>
                {selectedRow && <TrackDetailPanel track={selectedRow} />}
                <Dialog.Close asChild>
                  <Button
                    variant="soft"
                    size="3"
                    className="sm:hidden"
                    style={{
                      position: "sticky",
                      bottom: 0,
                      alignSelf: "flex-end",
                      marginTop: "auto",
                      backgroundColor: "var(--color-panel-solid)",
                    }}
                  >
                    <PanelRightClose size="16px" />
                    Close
                  </Button>
                </Dialog.Close>
              </Dialog.Content>
            </Theme>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
}
